import type { EventRecurrence } from "../domain/events/eventRecurrence";

const frequencyLabels: Record<string, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
};

export type NoticeRecurrenceSource = {
  eventRecurrence?: EventRecurrence;
  eventSeriesId?: string;
  eventSeriesIndex?: number;
  eventSeriesTotal?: number;
};

export function formatRecurrenceFrequency(frequency?: string): string | null {
  if (!frequency) return null;
  return frequencyLabels[frequency] || frequency;
}

export function formatRecurrenceUntil(until?: string): string | null {
  if (!until?.trim()) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(until.trim())) {
    const [year, month, day] = until.trim().split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
  return until.trim();
}

/** Recurrence rule lives on series index 0; resolve from siblings when needed. */
export function resolveNoticeEventRecurrence(
  notice: NoticeRecurrenceSource,
  allNotices?: NoticeRecurrenceSource[],
): EventRecurrence | undefined {
  if (notice.eventRecurrence?.frequency) {
    return notice.eventRecurrence;
  }
  if (!notice.eventSeriesId || !allNotices?.length) {
    return undefined;
  }
  const head = allNotices.find(
    (candidate) =>
      candidate.eventSeriesId === notice.eventSeriesId &&
      (candidate.eventSeriesIndex === 0 ||
        Boolean(candidate.eventRecurrence?.frequency)),
  );
  return head?.eventRecurrence;
}

export type EventRecurrenceDisplay = {
  frequencyLabel: string;
  endsLabel: string | null;
  occurrenceLabel: string | null;
};

export function getEventRecurrenceDisplay(
  recurrence: EventRecurrence | undefined,
  notice: NoticeRecurrenceSource,
): EventRecurrenceDisplay | null {
  if (!recurrence?.frequency) return null;

  const frequencyLabel = formatRecurrenceFrequency(recurrence.frequency);
  if (!frequencyLabel) return null;

  let endsLabel: string | null = null;
  if (recurrence.until) {
    endsLabel = formatRecurrenceUntil(recurrence.until);
  } else if (recurrence.count) {
    endsLabel = `After ${recurrence.count} occurrences`;
  }

  let occurrenceLabel: string | null = null;
  const total = notice.eventSeriesTotal;
  const index = notice.eventSeriesIndex;
  if (typeof total === "number" && total > 1) {
    if (typeof index === "number" && index > 0) {
      occurrenceLabel = `Occurrence ${index + 1} of ${total}`;
    } else {
      occurrenceLabel = `${total} dates in this series`;
    }
  }

  return { frequencyLabel, endsLabel, occurrenceLabel };
}
